import {FormEvent, useEffect, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {jsonInit} from '../api/client';
import ErrorBox, {FieldError, isGeneralError} from '../components/feedback/ErrorBox';
import Icon from '../components/Icon';
import {useAuth, useMe} from '../hooks/useAuth';
import {Hemisphere, Me} from '../types';
import Button from '../components/Button';

// First login (and "edit profile" later): a name and which half of the world the garden is in,
// so the Journal's season label and the Phase 3 nudges line up.
export default function WelcomeInPage() {
    const {call} = useAuth();
    const me = useMe();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState('');
    const [hemisphere, setHemisphere] = useState<Hemisphere | null>(null);

    useEffect(() => {
        if (me.data) {
            setFirstName(me.data.firstName ?? '');
            setHemisphere(me.data.hemisphere);
        }
    }, [me.data]);

    const save = useMutation({
        mutationFn: () => call<Me>('/api/auth/me', jsonInit('PUT', {firstName, hemisphere})),
        onSuccess: updated => {
            queryClient.setQueryData(['me'], updated);
            navigate('/journal', {replace: true});
        },
        meta: {silent: true}
    });

    const submit = (event: FormEvent) => {
        event.preventDefault();
        save.mutate();
    };

    const firstTime = me.data?.needsSetup ?? true;

    return (
        <form onSubmit={submit} className="settle-in flex flex-col gap-6 pt-10">
            <header className="flex flex-col gap-2">
                <h1 className="display">{firstTime ? 'Welcome in' : 'About you'}</h1>
                <p className="m-0 text-ink-2">
                    {firstTime ? 'Two quick things and the Journal is yours.' : 'Change what we call you or where your garden is.'}
                </p>
            </header>

            <label className="flex flex-col gap-1">
                <span className="meta">What should we call you?</span>
                <input
                    className="field"
                    type="text"
                    name="firstName"
                    autoComplete="given-name"
                    maxLength={60}
                    required
                    placeholder="First name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                />
            </label>

            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                <legend className="meta mb-1 p-0">Where is your garden?</legend>
                {(
                    [
                        ['S', 'Southern hemisphere', 'New Zealand, Australia, South Africa, South America'],
                        ['N', 'Northern hemisphere', 'UK, Europe, North America, Asia']
                    ] as const
                ).map(([value, label, hint]) => {
                    const selected = hemisphere === value;
                    return (
                        <button
                            key={value}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => setHemisphere(value)}
                            className={`flex min-h-[64px] items-center gap-3 rounded-md border-[1.5px] px-4 text-left ${
                                selected ? 'border-shoot bg-shoot-soft text-shoot-text' : 'border-line bg-surface'
                            }`}
                        >
                            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-pill ${selected ? 'bg-shoot text-on-shoot' : 'border-[1.5px] border-line'}`}>
                                {selected && <Icon name="check" size={16} strokeWidth={2.2} />}
                            </span>
                            <span>
                                <span className="block font-semibold">{label}</span>
                                <span className={`block text-xs ${selected ? '' : 'text-ink-2'}`}>{hint}</span>
                            </span>
                        </button>
                    );
                })}
            </fieldset>

            <FieldError error={save.error} field="firstName" />
            <FieldError error={save.error} field="hemisphere" />
            {isGeneralError(save.error) && <ErrorBox error={save.error} />}

            <Button type="submit" busy={save.isPending} busyLabel="Saving…" disabled={firstName.trim() === '' || hemisphere === null}>
                {firstTime ? 'Into the garden' : 'Save'}
            </Button>
        </form>
    );
}
